import React, {useCallback, useMemo} from 'react';
import {observer} from "mobx-react";
import {JACKPOT_WIDGET_LAYOUT} from '../jackpot-widget/jackpot-widget.component';
import {Check} from '@sparkware/app-utils';
import {useServices} from '../../react-hooks/use-services.hook';
import {
    GameImageContainerComponent
} from "../single-game/common/parts/game-image-container.component/game-image-container.component";
import {
    GameImageContainerBox
} from "../single-game/regular-template/boxes/game-image-container/game-image-container.box";
import {GameInfo, OpenGameParameters} from "../../services/games/game-info";
import {JackpotBetWidgetGamesListBox} from "./boxes/jackpot-bet-widget-games-list.box";
import {JackpotBetWidgetContentBox} from "./boxes/jackpot-bet-widget-content.box";
import {JackpotBetWidgetGameFlexContainerBox} from "./boxes/jackpot-bet-widget-game-flex-container.box";
import {JackpotBetWidgetBgBox} from "./boxes/background-box/jackpot-bet-widget-bg.box";
import {
    JackpotBetWidgetConfiguration
} from "../../services/configurations/config-data-sections/jackpot-bets-widget.configuration";
import {JackpotBetWidgetLogoBox} from "./boxes/logo-box/jackpot-bet-widget-logo.box";
import {JackpotBetWinHighlight} from '../jackpot-bets/jackpot-bet-live-win-highlight/jackpot-bet-win-highlight.component';
import {JackpotBetWinHighlightLabelBox} from '../jackpot-bets/jackpot-bet-live-win-highlight/boxes/jackpot-bet-win-highlight-label/jackpot-bet-win-highlight-label.box';
import {JackpotBetWinHighlightAmountBox} from '../jackpot-bets/jackpot-bet-live-win-highlight/boxes/jackpot-bet-win-highlight-amount/jackpot-bet-win-highlight-amount.box';

interface JackpotBetWidgetComponentProps {
    WrapperBox?: React.ComponentType;
    widgetConfig: JackpotBetWidgetConfiguration;
    gameTypes?: number[];
    useSquareIcons?: boolean;
    listName?: string;
    arenaName?: string;
}

const ComponentBody: React.FC<JackpotBetWidgetComponentProps> = observer((props) => {
    const services = useServices();

    const getJackpotWidgetLayout = (): JACKPOT_WIDGET_LAYOUT => {
        const isMobileTablet = services.mediaQuery.mAndAbove && services.mediaQuery.lAndBelow;

        return isMobileTablet ? JACKPOT_WIDGET_LAYOUT.PORTRAIT_TALL : JACKPOT_WIDGET_LAYOUT.PORTRAIT;
    }

    const renderGames = (): React.ReactNode => {
        const isRenderedSpecialWidget = Check.isEmpty(props.gameTypes);
        if (isRenderedSpecialWidget) {
            return null;
        }

        const openGame = async (gameInfo: GameInfo) => {
            const openGameParams: OpenGameParameters = {
                isRealMode: true,
                listTitle: props.listName,
                pageName: props.arenaName
            };
            await gameInfo.open(openGameParams)
        }

        //Slice 0, 10 because product wants us to only show 10 games on mobile regardless of admin config. Mb move this in model?
        return (
            <JackpotBetWidgetContentBox>
                <JackpotBetWidgetGamesListBox className={'cy-jackpot-bet-mobile-game-list'}>
                    {props.gameTypes.slice(0, 10).map(gameType => {
                        const gameInfo = services.games.gamesMap[gameType];

                        if (!gameInfo) {
                            return null;
                        }

                        return (
                            <JackpotBetWidgetGameFlexContainerBox key={`JackpotbetWidget_${gameType}`}>
                                <GameImageContainerComponent gameInfo={gameInfo}
                                    box={GameImageContainerBox}
                                    onOpenRealGame={() => openGame(gameInfo)}
                                    useSquareIcons={Boolean(props.useSquareIcons)}
                                    playRealGameButton={null}
                                />
                            </JackpotBetWidgetGameFlexContainerBox>
                        );
                    })}
                </JackpotBetWidgetGamesListBox>
            </JackpotBetWidgetContentBox>
        );
    }

    if (!props.widgetConfig?.display) {
        return null;
    }

    const renderJackpotWidgetInfo = (): React.ReactNode => {
        return (
            <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%', gap: '1rem', margin: '1.6rem 0'}}>
                <div style={{fontSize: '1.2rem', fontWeight: '600'}}>
                    {services.translation.translate("JACKPOT_BET_AVAILABLE_GAMES")}
                </div>
                <div style={{fontSize: '1.2rem', fontWeight: '900'}}>
                    {services.translation.translate("Opt-in required")}
                </div>
            </div>
        );
    }

    // Wrap the function in useCallback to memoize it. 
    const renderJackpotBetFormattedMoney = useCallback((label: string, amount: number) => {
        let formattedMoney = services.currency.formatMoney(amount);
        const decimalSymbol = services.currency.getDecimalSymbol();
        const splittedMoney = formattedMoney.split(decimalSymbol);

        if (splittedMoney.length === 2) {
            return (<>
                <JackpotBetWinHighlightLabelBox>
                    {label}
                </JackpotBetWinHighlightLabelBox>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-end', fontSize: '4.8rem'}} className={'cy-jp-bets-widget-wins-today'}>
                    <JackpotBetWinHighlightAmountBox>{splittedMoney[0]}</JackpotBetWinHighlightAmountBox>
                    <JackpotBetWinHighlightAmountBox>{decimalSymbol}</JackpotBetWinHighlightAmountBox>
                    <JackpotBetWinHighlightAmountBox style={{fontSize: '75%', paddingBottom: '0.6rem'}}>{splittedMoney[1]}</JackpotBetWinHighlightAmountBox>
                </div>
            </>)
        } else {
            return (<>
                <JackpotBetWinHighlightLabelBox>
                    {label}
                </JackpotBetWinHighlightLabelBox>
                <JackpotBetWinHighlightAmountBox className={'cy-jp-bets-widget-wins-today'}>
                    {formattedMoney}
                </JackpotBetWinHighlightAmountBox>
            </>)
        }
    }, [services.currency]); 

    // Update the useMemo dependencies to include the memoized function and the MobX object.
    const availablePrizes = useMemo(() => {
        const prizes = (services.jackpotBet.jackpotsInfo.getPrizes() ?? []).map(prize => ({
            render: () => renderJackpotBetFormattedMoney(prize.label, prize.amount)
        }));

        return [
            ...prizes,
            {
                render: () => (
                    <>
                        <JackpotBetWinHighlightLabelBox>
                            {services.translation.translate('JACKPOT_BET_WINNERS_TODAY')}
                        </JackpotBetWinHighlightLabelBox>
                        <JackpotBetWinHighlightAmountBox className="cy-jp-bets-widget-winners-count">
                            {services.jackpotBet.jackpotsInfo.winnersCount.toString()}
                        </JackpotBetWinHighlightAmountBox>
                    </>
                )
            },
            {
                render: () =>
                    renderJackpotBetFormattedMoney(
                        services.translation.translate('JACKPOT_BET_WON_TODAY'),
                        services.jackpotBet.jackpotsInfo.totalWinsToday
                    )
            }
        ];
    }, [services.jackpotBet.jackpotsInfo, renderJackpotBetFormattedMoney, services.translation]);


    const WrapperBox = props.WrapperBox || React.Fragment;
    return (
        <WrapperBox>
            <JackpotBetWidgetBgBox layout={getJackpotWidgetLayout()}>
                <JackpotBetWidgetLogoBox />
                <JackpotBetWinHighlight availablePrizes={availablePrizes} />
                {renderJackpotWidgetInfo()}
                {renderGames()}
            </JackpotBetWidgetBgBox>
        </WrapperBox>
    );
});

export const JackpotBetWidgetComponent = ComponentBody;